import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  List,
  ListItem,
  TextField,
} from "@mui/material";
import BaseModal from "../BaseModal/index.tsx";
import Loading from "../../Loading/index.tsx";
import Button from "../../../ui/Button.tsx";
import adminService from "../../../services/adminService.ts";
import { useFeedback } from "../../../contexts/FeedbackContext.tsx";

// Mesma receita de cores dos outros modais admin (ex: AlterarLiberacaoModal) —
// sem ThemeProvider do MUI, label/borda/texto do TextField caem no tema claro
// padrão do MUI, sem contraste no dark mode.
const fieldSx = {
  "& .MuiInputLabel-root": { color: "var(--ink-faint)" },
  "& .MuiInputLabel-root.Mui-focused": { color: "var(--blue)" },
  "& .MuiOutlinedInput-root": {
    color: "var(--ink)",
    "& fieldset": { borderColor: "var(--border-strong)" },
    "&:hover fieldset": { borderColor: "var(--border-strong)" },
    "&.Mui-focused fieldset": { borderColor: "var(--blue)" },
  },
  "& .MuiInputBase-input::placeholder": { color: "var(--ink-faint)", opacity: 1 },
};

interface EmpresaAdmin {
  id: string;
  nome: string;
}

interface Impacto {
  pacientes: number;
  medicos: number;
  instrumentadores: number;
  hospitais: number;
  convenios: number;
  tiposCirurgia: number;
  cirurgias: number;
  planos: number;
  faturas: number;
  usuariosExclusivos: number;
  usuariosCompartilhados: number;
  pareceClienteReal: boolean;
}

interface ExcluirEmpresaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExcluida: (empresaId: string) => void;
  empresa: EmpresaAdmin | null;
}

const ExcluirEmpresaModal: React.FC<ExcluirEmpresaModalProps> = ({
  isOpen,
  onClose,
  onExcluida,
  empresa,
}) => {
  const { showFeedback } = useFeedback();
  const [impacto, setImpacto] = useState<Impacto | null>(null);
  const [carregandoImpacto, setCarregandoImpacto] = useState(false);
  const [confirmacao, setConfirmacao] = useState("");
  const [excluindo, setExcluindo] = useState(false);

  useEffect(() => {
    if (isOpen && empresa) {
      setImpacto(null);
      setConfirmacao("");
      setCarregandoImpacto(true);
      adminService
        .calcularImpactoExclusao(empresa.id)
        .then(setImpacto)
        .finally(() => setCarregandoImpacto(false));
    }
  }, [isOpen, empresa]);

  if (!empresa) return null;

  const confirmarTextoOk = confirmacao.trim() === empresa.nome.trim();

  const handleExcluir = async () => {
    setExcluindo(true);
    try {
      await adminService.excluirEmpresa(empresa.id);
      onExcluida(empresa.id);
      showFeedback("Empresa excluída com sucesso!", "success");
      onClose();
    } catch (error: any) {
      showFeedback(error?.message || "Erro ao excluir empresa", "error");
    } finally {
      setExcluindo(false);
    }
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title={`Excluir empresa — ${empresa.nome}`}>
      <Box display="flex" flexDirection="column" gap={2.5} width={420} maxWidth="100%">
        {carregandoImpacto || !impacto ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <Loading />
          </Box>
        ) : (
          <>
            {impacto.pareceClienteReal && (
              <Alert severity="warning">
                Esta empresa tem sinais de cliente real (pago, assinatura ASAAS ativa ou
                liberação de longo prazo). Confirme que é mesmo teste antes de excluir.
              </Alert>
            )}

            <Alert severity="error" icon={false}>
              <AlertTitle sx={{ fontWeight: 600 }}>Isso vai excluir permanentemente</AlertTitle>
              <List dense disablePadding sx={{ "& .MuiListItem-root": { py: 0.25, display: "list-item", pl: 0, ml: 2.5 } }}>
                <ListItem disableGutters>{impacto.pacientes} paciente(s)</ListItem>
                <ListItem disableGutters>{impacto.medicos} médico(s)</ListItem>
                <ListItem disableGutters>{impacto.instrumentadores} instrumentador(es)</ListItem>
                <ListItem disableGutters>{impacto.hospitais} hospital(is)</ListItem>
                <ListItem disableGutters>{impacto.convenios} convênio(s)</ListItem>
                <ListItem disableGutters>{impacto.tiposCirurgia} tipo(s) de cirurgia</ListItem>
                <ListItem disableGutters>{impacto.cirurgias} cirurgia(s)</ListItem>
                <ListItem disableGutters>{impacto.planos} plano(s)</ListItem>
                <ListItem disableGutters>{impacto.faturas} fatura(s)</ListItem>
                <ListItem disableGutters>
                  {impacto.usuariosExclusivos} usuário(s) exclusivo(s) desta empresa (serão excluídos)
                </ListItem>
                <ListItem disableGutters>
                  {impacto.usuariosCompartilhados} usuário(s) compartilhado(s) com outra empresa (só o vínculo é removido)
                </ListItem>
              </List>
            </Alert>

            <p style={{ color: "var(--ink)", fontSize: 13.2, margin: 0 }}>
              Para confirmar, digite o nome da empresa (<strong>{empresa.nome}</strong>) abaixo:
            </p>
            <TextField
              size="small"
              fullWidth
              sx={fieldSx}
              value={confirmacao}
              onChange={(e) => setConfirmacao(e.target.value)}
              placeholder={empresa.nome}
            />
            <Button
              variant="danger"
              disabled={!confirmarTextoOk || excluindo}
              onClick={handleExcluir}
              style={{ width: "100%" }}
            >
              {excluindo ? <Loading color="white" /> : "Excluir definitivamente"}
            </Button>
          </>
        )}
      </Box>
    </BaseModal>
  );
};

export default ExcluirEmpresaModal;
